import { useState } from 'react';
import { MapPin, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AddressForm, { AddressFormData } from './AddressForm';
import { Address } from './AddressBook';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface AddressSelectorProps {
  addresses: Address[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAddressesChange: () => void;
  title: string;
  type: 'shipping' | 'billing';
  showAddNew?: boolean;
  loading?: boolean;
  onCityChange?: (city: string) => void;
  saveButtonText?: string;
  showCancelButton?: boolean;
  onGuestAddressSave?: (address: Address) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

const AddressSelector = ({
  addresses,
  selectedId,
  onSelect,
  onAddressesChange,
  title,
  type,
  showAddNew = true,
  loading = false,
  onCityChange,
  saveButtonText,
  showCancelButton,
  onEditingChange,
  onGuestAddressSave
}: AddressSelectorProps) => {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [deletingAddress, setDeletingAddress] = useState<Address | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const saveAddress = async (data: AddressFormData) => {
    setSaving(true);
    try {
      // Handle guest checkout - save locally
      if (!user) {
        const guestAddress: Address = {
          id: `guest-${Date.now()}`,
          user_id: 'guest',
          ...data,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_used_at: new Date().toISOString()
        };

        if (onGuestAddressSave) {
          onGuestAddressSave(guestAddress);
          onSelect(guestAddress.id);

          if (onCityChange && type === 'shipping') {
            onCityChange(guestAddress.city);
          }

          toast.success('הכתובת נשמרה בהצלחה');
          setShowForm(false);
          setEditingAddress(null);
          onEditingChange?.(false);
        } else {
          console.error('onGuestAddressSave not provided for guest user');
          toast.error('שגיאה בשמירת הכתובת');
        }
        setSaving(false);
        return;
      }

      let targetAddressId: string | null = null;

      if (editingAddress) {
        // Update existing address
        const { error } = await (supabase as any)
          .from('addresses')
          .update(data)
          .eq('id', editingAddress.id);

        if (error) throw error;
        targetAddressId = editingAddress.id;
      } else {
        // Create new address
        const { data: newAddress, error } = await (supabase as any)
          .from('addresses')
          .insert({ ...data, user_id: user.id })
          .select()
          .single();

        if (error) throw error;

        if (newAddress) {
          targetAddressId = newAddress.id;
          onSelect(newAddress.id);
          // Notify parent of city change for shipping cost recalculation
          if (onCityChange && type === 'shipping') {
            onCityChange(newAddress.city);
          }
        }
      }

      // Update last_used_at for this address
      if (targetAddressId) {
        await (supabase as any)
          .from('addresses')
          .update({ last_used_at: new Date().toISOString() })
          .eq('id', targetAddressId)
          .eq('user_id', user.id);
      }

      setShowForm(false);
      setEditingAddress(null);
      onEditingChange?.(false);
      onAddressesChange();
    } catch (error) {
      console.error('Error saving address:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setShowForm(true);
    onEditingChange?.(true);
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setShowForm(true);
    onEditingChange?.(true);
  };

  const handleDeleteAddress = async () => {
    if (!deletingAddress) return;

    const wasSelected = selectedId === deletingAddress.id;

    try {
      const { error } = await (supabase as any)
        .from('addresses')
        .delete()
        .eq('id', deletingAddress.id);

      if (error) throw error;

      toast.success('כתובת נמחקה בהצלחה');

      // If the deleted address was selected, clear the selection
      if (wasSelected) {
        onSelect(''); // Clear selection in parent component
        if (onCityChange) {
          onCityChange(''); // Clear city as well
        }
      }

      onAddressesChange();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('שגיאה במחיקת הכתובת');
    } finally {
      setShowDeleteDialog(false);
      setDeletingAddress(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">טוען כתובות...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4" dir="rtl">
        <h3 className="text-lg font-semibold">{title}</h3>

        {addresses.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <AddressForm
                onSave={saveAddress}
                onCancel={() => {
                  setEditingAddress(null);
                  onEditingChange?.(false);
                }}
                isSaving={saving}
                initialData={editingAddress || {}}
                type={type}
              />
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3" dir="rtl">
              {addresses.map((address) => (
                <Card
                  key={address.id}
                  className={`cursor-pointer transition-all ${selectedId === address.id
                    ? 'border-2 border-lime bg-lime/5'
                    : 'border hover:border-lime/50'
                    }`}
                  onClick={() => {
                    onSelect(address.id);
                    const foundAddress = addresses.find(a => a.id === address.id);

                    // Notify parent of city change for shipping cost recalculation
                    if (onCityChange && type === 'shipping' && foundAddress) {
                      onCityChange(foundAddress.city);
                    }

                    if (user) {
                      (async () => {
                        try {
                          await (supabase as any)
                            .from('addresses')
                            .update({ last_used_at: new Date().toISOString() })
                            .eq('id', address.id)
                            .eq('user_id', user.id);
                        } catch (err) {
                          console.error('Error updating last used address:', err);
                        }
                      })();
                    }
                  }}
                >
                  <CardContent className="p-4" dir="rtl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        <MapPin className="h-4 w-4" />
                        <span className="font-medium">
                          {address.street_name} {address.street_number}, {address.city}
                        </span>
                      </div>
                      {!showForm && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAddress(address);
                            }}
                            className="h-7 w-7 p-0"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingAddress(address);
                              setShowDeleteDialog(true);
                            }}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1 text-sm text-right">
                      {address.details && (
                        <p className="text-muted-foreground">{address.details}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {showAddNew && !showForm && (
              <Button variant="outline" onClick={handleAddNew} className="w-full mt-4">
                <Plus className="h-4 w-4 ml-2" />
                הוסף כתובת חדשה
              </Button>
            )}

            {showForm && (
              <Card className="mt-4">
                <CardContent className="p-4">
                  <AddressForm
                    onSave={saveAddress}
                    onCancel={() => {
                      setShowForm(false);
                      setEditingAddress(null);
                      onEditingChange?.(false);
                    }}
                    isSaving={saving}
                    initialData={editingAddress || {}}
                    saveButtonText={saveButtonText}
                    showCancelButton={showCancelButton}
                    type={type}
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right">מחיקת כתובת</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              האם אתה בטוח שברצונך למחוק את הכתובת?
              <br />
              <span className="font-medium">
                {deletingAddress?.street_name} {deletingAddress?.street_number}, {deletingAddress?.city}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={handleDeleteAddress} className="bg-destructive hover:bg-destructive/90">
              מחק
            </AlertDialogAction>
            <AlertDialogCancel>ביטול</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AddressSelector;